// The committed-budget filter: read the Funnel for the projects whose budget
// sits between two USD figures.
//
// Two boxes rather than a segmented row, because a money bound is a number the
// reader types, not one of four buckets. They open on 0 to 100,000 so there is a
// range to edit rather than two empty fields, and a value left at its default is
// NOT a bound — nothing is sent until one differs, which keeps the untouched
// page on the cheap precomputed lane. Applied on Enter or blur; an inverted pair
// snaps back rather than applying, since a bound nothing satisfies draws an
// empty chart that reads as "no such projects".
//
// A project with no readable budget is KEPT by any bound (it is neither over
// nor under it), so the count funnel never quietly shrinks to the subset that
// named a number. The server owns that rule; the label beside the boxes says it.

import { useState } from "react";

import {
  type BudgetRange,
  budgetBounds,
  budgetFromInputs,
  isBudgetActive,
} from "../lib/budget-range";
import "./budget-filter.css";

type Props = {
  /** The bound in the caller's own state (URL). Both sides open = no bound. */
  budget: BudgetRange;
  onChange: (next: BudgetRange) => void;
};

export function BudgetFilter({ budget, onChange }: Props) {
  const bounds = budgetBounds(budget);
  const [minText, setMinText] = useState(String(bounds.min));
  const [maxText, setMaxText] = useState(String(bounds.max));

  const apply = () => {
    const next = budgetFromInputs(minText, maxText);
    if (next === undefined) {
      setMinText(String(bounds.min));
      setMaxText(String(bounds.max));
      return;
    }
    onChange(next);
  };
  const onKeyDown = (e: { key: string }) => {
    if (e.key === "Enter") apply();
  };
  const reset = () => {
    const cleared = budgetBounds({});
    setMinText(String(cleared.min));
    setMaxText(String(cleared.max));
    onChange({});
  };

  return (
    <div className="bd-filter">
      <span className="bd-filter__label">Budget (USD)</span>
      <div className="bd-filter__range" role="group" aria-label="Budget (USD)">
        <input
          className="bd-filter__input"
          type="number"
          inputMode="decimal"
          min={0}
          aria-label="Budget minimum (USD)"
          value={minText}
          onChange={(e) => setMinText(e.target.value)}
          onBlur={apply}
          onKeyDown={onKeyDown}
        />
        <span className="bd-filter__to">to</span>
        <input
          className="bd-filter__input"
          type="number"
          inputMode="decimal"
          min={0}
          aria-label="Budget maximum (USD)"
          value={maxText}
          onChange={(e) => setMaxText(e.target.value)}
          onBlur={apply}
          onKeyDown={onKeyDown}
        />
        {isBudgetActive(budget) ? (
          <button type="button" className="bd-filter__reset" onClick={reset}>
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}
